// public/js/features/display/template-store.js
// Configurable display templates (docs/asset-display-templates.md).
//
// WHY this module exists: which asset fields appear WHERE used to be hard-coded
// in each surface (assets list columns, map info card, map pin label, asset
// detail). This store is the single place that resolves "what should this
// surface show for the current tenant and the current user", so the render
// paths stay dumb and unchanged in shape.
//
// Resolution model — "the user follows the tenant until they change something":
//   tenant default (tenant record, `displayTemplates`)   ← admin, applies to all
//   user override  (user settings, `displayTemplates`)   ← wins, per surface
// A user with no personal copy always sees the CURRENT tenant default, so an
// admin edit reaches everyone automatically. The override is stored per surface
// (not merged field-by-field): one surface can be personalised while the other
// three keep following the tenant.
//
// Everything degrades to the built-in DEFAULTS below, which reproduce today's
// hard-coded UI exactly — so callers behave identically until someone edits.
//
// BACKEND DEPENDENCY (see the note at saveTenantTemplate): the tenant PATCH
// whitelist in utils/dynamodb/tenants.mjs must accept `displayTemplates`, and
// handlers/tenants.mjs handleTenantFieldConfig should project it so non-admin
// members can read their tenant's default. Both are outside this module; the
// save path detects a silently-dropped attribute and reports it instead of
// pretending success.

import { loadTenants, loadTenantFieldConfig, updateTenant } from '../../modules/tenants.js';
import { loadAssetFields, visibleFields, effectiveFieldLabel, optionLabel } from '../assets/asset-fields.js';
import { resolveFieldLabels, customTextFields, fixedTextFields } from '../assets/field-labels.js';
import { getCurrentUser, can } from '../../modules/rbac.js';
import { formatLocalTime, formatTimeAgo } from '../../utils/asset-utils.js';
import { escapeHtml } from '../../utils/escape.js';

// ── Surfaces ────────────────────────────────────────────────────────────────
export const SURFACES = {
  listColumns: 'listColumns',
  mapCard:     'mapCard',
  pinLabel:    'pinLabel',
  assetDetail: 'assetDetail',
};

export const SURFACE_LIST = Object.values(SURFACES);

// Human copy for the settings tab + the editor header. Kept here so a new
// surface only has to be described once.
export const SURFACE_META = {
  [SURFACES.listColumns]: {
    title: 'Assets list columns',
    icon: 'fa-table-columns',
    description: 'Which columns the assets table shows, and in which order.',
  },
  [SURFACES.mapCard]: {
    title: 'Map asset card',
    icon: 'fa-id-card',
    description: 'The layout of the info card that opens when you click an asset on the map.',
  },
  [SURFACES.pinLabel]: {
    title: 'Map pin labels',
    icon: 'fa-map-pin',
    description: 'The single line of text printed under each pin on the map.',
  },
  [SURFACES.assetDetail]: {
    title: 'Asset detail fields',
    icon: 'fa-rectangle-list',
    description: 'Which fields the asset detail page lists, and in which order.',
  },
};

// ── Ordering bands ──────────────────────────────────────────────────────────
// Dynamic columns/fields (registry dropdowns, tenant custom text fields) cannot
// be listed in a static default — they differ per tenant. They are slotted into
// reserved bands so an unconfigured tenant gets exactly today's order, and a
// newly enabled field lands in a sensible place instead of at the very end.
const BAND = { registry: 100, custom: 200 };

// Pin labels must stay readable next to the marker, so the cap itself is capped.
export const PIN_LABEL_HARD_MAX = 40;

// ── Default templates (must mirror today's hard-coded UI) ───────────────────
// listColumns: the order buildColumns() in features/assets/list-view.js emits.
// `photo` is listed but hidden — the photo column ships with asset files
// (docs/asset-files.md §8); having the key here means it only has to be ticked.
const DEFAULT_LIST_COLUMNS = {
  columns: [
    { key: 'statusdot',        visible: true,  order: 10 },
    { key: 'photo',            visible: false, order: 20 },
    { key: 'engine_serialNo',  visible: true,  order: 30 },
    { key: 'reference',        visible: true,  order: 40 },
    { key: 'label',            visible: true,  order: 50 },
    { key: 'serial',           visible: true,  order: 60 },
    // registry fields  → band 100…
    // custom text fields → band 200…
    { key: 'tenant',           visible: true,  order: 300 },
    { key: 'lastPosition',     visible: true,  order: 310 },
    { key: 'lastUpdated',      visible: true,  order: 320 },
    { key: 'devices',          visible: true,  order: 330 },
    { key: 'actions',          visible: true,  order: 999 },
  ],
};

// mapCard: an ordered list of blocks. Only the templated part of the card lives
// here — the header controls (zoom/close) and the position/accuracy block stay
// hard-coded in modules/map/info-panel.js.
// The text block starts empty on purpose: the card header shows the reference
// and the line under it the name, so the old ${reference}/${label} default
// printed both of them a second time. A tenant that saved those lines keeps
// them - it can clear them in Settings > Display.
const DEFAULT_MAP_CARD = {
  blocks: [
    { type: 'title',   visible: true },
    { type: 'photo',   visible: false },
    { type: 'text',    visible: true, lines: [] },
    { type: 'sensors', visible: true },
    { type: 'buttons', visible: true },
  ],
};

// pinLabel: today the marker prints formatAssetLabel() truncated to 16 chars.
// The template default is the plain label; the cap keeps pins readable.
const DEFAULT_PIN_LABEL = { template: '${label}', maxLength: 16 };

// assetDetail: the field order of renderBasicFields() in asset-detail-view.js.
const DEFAULT_ASSET_DETAIL = {
  fields: [
    { key: 'label',     visible: true, order: 10 },
    { key: 'reference', visible: true, order: 20 },
    { key: 'serial',    visible: true, order: 30 },
    // registry fields  → band 100…
    // custom text fields → band 200…
    { key: 'status',    visible: true, order: 300 },
    { key: 'tenantId',  visible: true, order: 310 },
  ],
};

const DEFAULTS = {
  [SURFACES.listColumns]: DEFAULT_LIST_COLUMNS,
  [SURFACES.mapCard]:     DEFAULT_MAP_CARD,
  [SURFACES.pinLabel]:    DEFAULT_PIN_LABEL,
  [SURFACES.assetDetail]: DEFAULT_ASSET_DETAIL,
};

const clone = (v) => JSON.parse(JSON.stringify(v));

/** A pristine copy of the built-in default for a surface (never the shared object). */
export function defaultTemplate(surface) {
  return clone(DEFAULTS[surface] || {});
}

// ── Module state (one resolved config at a time = the active tenant scope) ───
const _cache = new Map();       // tenantId|'' → { tenant, user, resolved }
let _tenantId = null;           // tenant the resolved config belongs to
let _tenantTpl = {};            // raw tenant defaults, per surface
let _userTpl = {};              // raw user overrides, per surface
let _resolved = { ...clone(DEFAULTS) };
let _tenantRecord = null;       // tenant record (labels/visibility) for the palette
let _regDefs = [];              // asset-field registry defs (option value → label)
let _loadedOnce = false;
let _inflight = null;

// ── Normalisation ───────────────────────────────────────────────────────────
// Stored templates are user data: never trust their shape. Every accessor goes
// through here so a hand-edited or half-migrated record cannot break a render.
function normaliseOrderedKeys(value, fallback, listKey) {
  const raw = Array.isArray(value?.[listKey]) ? value[listKey] : null;
  if (!raw) return { [listKey]: clone(fallback) };
  const seen = new Set();
  const out = [];
  raw.forEach((entry, i) => {
    const key = typeof entry === 'string' ? entry : entry?.key;
    if (!key || typeof key !== 'string' || seen.has(key)) return;
    seen.add(key);
    const order = Number(entry?.order);
    out.push({
      key,
      visible: entry?.visible !== false,
      order: Number.isFinite(order) ? order : (i + 1) * 10,
    });
  });
  if (!out.length) return { [listKey]: clone(fallback) };
  out.sort((a, b) => a.order - b.order);
  return { [listKey]: out };
}

const BLOCK_TYPES = ['title', 'photo', 'text', 'sensors', 'buttons'];
// The title block may carry its own one-line template; empty = the asset label.
const TITLE_MAX = 120;

function normaliseMapCard(value) {
  const raw = Array.isArray(value?.blocks) ? value.blocks : null;
  if (!raw) return clone(DEFAULT_MAP_CARD);
  const blocks = [];
  for (const b of raw) {
    const type = typeof b?.type === 'string' ? b.type : null;
    if (!type || !BLOCK_TYPES.includes(type)) continue;
    const block = { type, visible: b?.visible !== false };
    if (type === 'title' && typeof b?.template === 'string') {
      block.template = b.template.replace(/[\r\n]+/g, ' ').slice(0, TITLE_MAX);
    }
    if (type === 'text') {
      block.lines = (Array.isArray(b?.lines) ? b.lines : [])
        .filter(l => typeof l === 'string')
        .map(l => l.slice(0, 300))
        .slice(0, 20);
    }
    blocks.push(block);
  }
  return blocks.length ? { blocks } : clone(DEFAULT_MAP_CARD);
}

function normalisePinLabel(value) {
  const tpl = typeof value?.template === 'string' ? value.template.replace(/[\r\n]+/g, ' ').slice(0, 200) : null;
  const max = Number(value?.maxLength);
  return {
    template: tpl && tpl.trim() ? tpl : DEFAULT_PIN_LABEL.template,
    maxLength: Number.isFinite(max) ? Math.min(PIN_LABEL_HARD_MAX, Math.max(6, Math.round(max))) : DEFAULT_PIN_LABEL.maxLength,
  };
}

/** Coerce any stored value for `surface` into a safe, complete template. */
export function normaliseTemplate(surface, value) {
  switch (surface) {
    case SURFACES.listColumns: return normaliseOrderedKeys(value, DEFAULT_LIST_COLUMNS.columns, 'columns');
    case SURFACES.assetDetail: return normaliseOrderedKeys(value, DEFAULT_ASSET_DETAIL.fields, 'fields');
    case SURFACES.mapCard:     return normaliseMapCard(value);
    case SURFACES.pinLabel:    return normalisePinLabel(value);
    default:                   return clone(value || {});
  }
}

function resolveAll() {
  const out = {};
  for (const s of SURFACE_LIST) {
    const raw = _userTpl[s] !== undefined ? _userTpl[s] : _tenantTpl[s];
    out[s] = normaliseTemplate(s, raw);
  }
  _resolved = out;
  return out;
}

// ── Loading ─────────────────────────────────────────────────────────────────
async function fetchUserSettings() {
  try {
    const res = await window.authenticatedFetch('api/v1/settings');
    if (!res.ok) return {};
    const data = await res.json();
    return data.settings || {};
  } catch { return {}; }
}

// The tenant record: admins may read the full record; plain members only get the
// slim white-label projection. Either is fine — we just need `displayTemplates`
// plus the label/visibility maps the variable palette is generated from.
async function fetchTenantRecord(tenantId) {
  if (!tenantId || tenantId === 'unassigned') return null;
  const groups = getCurrentUser()?.groups || [];
  const order = can(groups, 'listTenants') ? [loadTenants, loadTenantFieldConfig] : [loadTenantFieldConfig, loadTenants];
  for (const fn of order) {
    try {
      const list = await fn();
      const hit = (list || []).find(t => t.tenantId === tenantId);
      if (hit) return hit;
    } catch { /* try the next endpoint */ }
  }
  return null;
}

/**
 * Resolve the effective config for a tenant: tenant default + user override.
 * Cached in memory per tenant — hot render paths use the synchronous
 * getTemplate() and never await.
 * @param {string|null} tenantId  active tenant scope ('unassigned'/null → user override + defaults)
 * @param {{force?: boolean}} [opts]
 */
export async function loadTemplates(tenantId = null, { force = false } = {}) {
  const key = tenantId || '';
  if (!force && _cache.has(key)) {
    const c = _cache.get(key);
    _tenantId = tenantId; _tenantTpl = c.tenant; _userTpl = c.user; _tenantRecord = c.record;
    return resolveAll();
  }
  if (_inflight && _inflight.key === key && !force) return _inflight.promise;

  const promise = (async () => {
    const [settings, record, regDefs] = await Promise.all([
      fetchUserSettings(),
      fetchTenantRecord(tenantId),
      loadAssetFields().catch(() => []),
    ]);
    _tenantId    = tenantId;
    _tenantRecord = record;
    _regDefs     = Array.isArray(regDefs) ? regDefs : [];
    _tenantTpl   = (record && typeof record.displayTemplates === 'object' && record.displayTemplates) || {};
    _userTpl     = (settings && typeof settings.displayTemplates === 'object' && settings.displayTemplates) || {};
    _cache.set(key, { tenant: _tenantTpl, user: _userTpl, record });
    _loadedOnce = true;
    return resolveAll();
  })();

  _inflight = { key, promise };
  try { return await promise; }
  finally { if (_inflight && _inflight.key === key) _inflight = null; }
}

/** Drop the cache so the next loadTemplates() re-reads tenant + user settings. */
export function invalidateTemplates() {
  _cache.clear();
  _loadedOnce = false;
}

/**
 * Synchronous accessor for the resolved template of a surface — safe to call
 * from a hot render path. Returns the built-in default when nothing has been
 * loaded or configured, so callers work unchanged out of the box.
 * Treat the result as read-only.
 */
export function getTemplate(surface) {
  return _resolved[surface] || defaultTemplate(surface);
}

// Debug hook: the resolved templates are module state, which makes "why is my
// template not applied?" impossible to answer from the console. Read-only.
if (typeof window !== 'undefined') {
  window.__displayTemplates = {
    resolved: () => clone(_resolved),
    tenant:   () => clone(_tenantTpl),
    user:     () => clone(_userTpl),
    scope:    () => _tenantId,
    loaded:   () => _loadedOnce,
  };
}

export function isLoaded() { return _loadedOnce; }
export function getScopeTenantId() { return _tenantId; }

/** The tenant record behind the resolved config (labels/visibility for the palette). */
export function getLabelTenant() { return _tenantRecord; }

/** The asset-field registry definitions loaded alongside the templates. */
export function getRegistryDefs() { return _regDefs; }

/** The tenant default for a surface (normalised) — what "Reset to tenant default" restores. */
export function getTenantTemplate(surface) {
  return normaliseTemplate(surface, _tenantTpl[surface]);
}

/** Does the current user have a personal copy of this surface? */
export function hasUserOverride(surface) {
  return Object.prototype.hasOwnProperty.call(_userTpl, surface) && _userTpl[surface] != null;
}

// ── Saving ──────────────────────────────────────────────────────────────────
async function patchUserSettings(displayTemplates) {
  const res = await window.authenticatedFetch('api/v1/settings', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ displayTemplates }),
  });
  if (!res.ok) throw new Error('Failed to save your display settings');
  return res.json().catch(() => ({}));
}

function syncCache() {
  _cache.set(_tenantId || '', { tenant: _tenantTpl, user: _userTpl, record: _tenantRecord });
  resolveAll();
}

/**
 * Save the TENANT default for one surface (admin action, affects every user of
 * the tenant who has no personal override).
 *
 * NOTE: `displayTemplates` must be present in the PATCH whitelist of
 * utils/dynamodb/tenants.mjs — without it DynamoDB silently drops the attribute.
 * We compare the echoed tenant back and raise a clear error instead of showing a
 * success toast for a write that never happened.
 */
export async function saveTenantTemplate(tenantId, surface, value) {
  if (!tenantId || tenantId === 'unassigned') throw new Error('Pick a single tenant first');
  if (!SURFACE_LIST.includes(surface)) throw new Error(`Unknown surface: ${surface}`);
  const next = { ..._tenantTpl, [surface]: normaliseTemplate(surface, value) };
  const res = await updateTenant(tenantId, { displayTemplates: next });
  const echoed = res?.tenant?.displayTemplates;
  if (echoed && !echoed[surface]) {
    throw new Error('The server did not store the tenant default (displayTemplates is not accepted yet)');
  }
  _tenantTpl = next;
  syncCache();
  return next[surface];
}

/** Save the current user's personal copy of one surface (takes precedence). */
export async function saveUserTemplate(surface, value) {
  if (!SURFACE_LIST.includes(surface)) throw new Error(`Unknown surface: ${surface}`);
  if (!_loadedOnce) await loadTemplates(_tenantId);
  const next = { ..._userTpl, [surface]: normaliseTemplate(surface, value) };
  await patchUserSettings(next);
  _userTpl = next;
  syncCache();
  return next[surface];
}

/** Drop the personal copy so the (current, live) tenant default applies again. */
export async function resetUserTemplate(surface) {
  if (!_loadedOnce) await loadTemplates(_tenantId);
  if (!hasUserOverride(surface)) return getTemplate(surface);
  const next = { ..._userTpl };
  delete next[surface];
  // Send the full object: the settings PATCH merges TOP-LEVEL keys only, so a
  // removed surface has to be absent from the whole displayTemplates value.
  await patchUserSettings(next);
  _userTpl = next;
  syncCache();
  return getTemplate(surface);
}

// ── Variables ───────────────────────────────────────────────────────────────
// Fixed (non-registry) variables. `get` reads the value off an asset record;
// `sample` feeds the palette + the live preview when a real value is missing.
const FIXED_VARS = [
  { id: 'reference',    group: 'Identification', labelKey: 'reference', sample: 'N512SB' },
  { id: 'label',        group: 'Identification', labelKey: 'label',     sample: 'CFM56-7B Engine' },
  { id: 'serial',       group: 'Identification', labelKey: 'serial',    sample: 'SN-77219' },
  { id: 'assetUUID',    group: 'Identification', label: 'Asset UUID',   sample: 'a17c8e42-…' },
  { id: 'status',       group: 'Status & time',  label: 'Status',       sample: 'active' },
  { id: 'type',         group: 'Status & time',  label: 'Asset type',   sample: 'engine' },
  { id: 'tenantName',   group: 'Status & time',  label: 'Tenant',       sample: 'Superb Aviation' },
  { id: 'lastSeen',     group: 'Status & time',  label: 'Last seen (relative)', sample: '2 hours ago' },
  { id: 'lastUpdated',  group: 'Status & time',  label: 'Last updated (date)',  sample: '01.09.2026, 08:14' },
  { id: 'lastFix',      group: 'Status & time',  label: 'Last position fix (relative)', sample: '28 hours ago' },
  { id: 'lastLat',      group: 'Position',       label: 'Latitude',     sample: '47.45102' },
  { id: 'lastLon',      group: 'Position',       label: 'Longitude',    sample: '8.56413' },
  { id: 'deviceCount',  group: 'Position',       label: 'Paired devices', sample: '2' },
];

const FIXED_VAR_IDS = new Set(FIXED_VARS.map(v => v.id));

function fixedValue(id, asset) {
  switch (id) {
    case 'assetUUID':   return asset.assetUUID || asset.assetId || '';
    case 'status':      return asset.status || '';
    case 'type':        return asset.type || '';
    case 'tenantName':  return asset.tenantName || '';
    case 'lastSeen':    return asset.lastUpdated ? formatTimeAgo(asset.lastUpdated) : '';
    case 'lastUpdated': return asset.lastUpdated ? formatLocalTime(asset.lastUpdated) : '';
    case 'lastFix':     return asset.lastFixAt ? formatTimeAgo(asset.lastFixAt) : (asset.lastUpdated ? formatTimeAgo(asset.lastUpdated) : '');
    case 'lastLat':     return asset.lastLat != null && asset.lastLat !== '' ? String(asset.lastLat) : '';
    case 'lastLon':     return asset.lastLon != null && asset.lastLon !== '' ? String(asset.lastLon) : '';
    case 'deviceCount': return asset.assignedDevices?.length ? String(asset.assignedDevices.length) : '';
    default:            return asset[id] != null ? String(asset[id]) : '';
  }
}

/**
 * The variable palette for the editors: fixed identification/status fields
 * (labelled with the tenant's white-label names) + every registry field the
 * tenant has switched on + the enabled custom/fixed free-text fields.
 * @returns {Array<{id:string,label:string,sample:string,group:string}>}
 */
export function availableVariables(regDefs = _regDefs, tenant = _tenantRecord) {
  const L = resolveFieldLabels(tenant);
  const out = FIXED_VARS.map(v => ({
    id: v.id,
    label: v.labelKey ? L[v.labelKey] : v.label,
    sample: v.sample,
    group: v.group,
  }));
  for (const f of visibleFields(regDefs, tenant)) {
    out.push({
      id: f.fieldId,
      label: effectiveFieldLabel(f, tenant),
      sample: (f.options || [])[0]?.label || 'Value',
      group: 'Asset fields',
    });
  }
  for (const cf of [...fixedTextFields(tenant), ...customTextFields(tenant)].filter(c => c.enabled)) {
    out.push({ id: cf.key, label: cf.label, sample: cf.key === 'engine_serialNo' ? '779214' : 'Value', group: 'Text fields' });
  }
  return out;
}

// A plausible asset used by the editor previews when the caller has none.
export const SAMPLE_ASSET = Object.freeze({
  assetUUID: 'a17c8e42-3f90-4b21-9c55-0f61d0b2a7e3',
  reference: 'N512SB',
  label: 'CFM56-7B Engine',
  serial: 'SN-77219',
  engine_serialNo: '779214',
  status: 'active',
  type: 'engine',
  tenantName: 'Superb Aviation',
  lastLat: '47.45102',
  lastLon: '8.56413',
  lastUpdated: new Date(Date.now() - 2 * 3600 * 1000).toISOString(),
  assignedDevices: [{ deviceId: 'DM-1042', serialNumber: '1042' }, { deviceId: 'BLE-77A2' }],
});

// ── Template rendering ──────────────────────────────────────────────────────
const TOKEN_RE = /\$\{\s*([A-Za-z0-9_]+)\s*\}/g;

function resolveVariable(id, asset, custom) {
  if (!asset) return '';
  if (typeof custom === 'function') {
    const v = custom(id, asset);
    if (v !== undefined) return v == null ? '' : String(v);
  }
  if (FIXED_VAR_IDS.has(id)) return fixedValue(id, asset);
  // Registry dropdown → show the option LABEL, never the stored value code.
  const def = (_regDefs || []).find(f => f.fieldId === id);
  if (def) return optionLabel(def, asset[id]) || '';
  const raw = asset[id];
  return raw == null || raw === '' ? '' : String(raw);
}

/**
 * Expand `${fieldId}` placeholders in a template against an asset record.
 *
 * Rules (docs/asset-display-templates.md): an unknown or empty variable renders
 * as an empty string — never "undefined" — and a line whose variables are ALL
 * empty collapses, so a card never shows a stray separator for missing data.
 *
 * @param {string} templateString  one or more lines, literal text + ${vars}
 * @param {object} asset
 * @param {object} [helpers]
 * @param {(id:string, asset:object)=>string|undefined} [helpers.resolve] custom value lookup (return undefined to fall through)
 * @param {boolean} [helpers.plain=false] return plain text instead of escaped HTML
 * @param {string}  [helpers.joiner='<br>'] how surviving lines are joined
 * @param {number}  [helpers.maxLength=0] cap the rendered text (adds an ellipsis)
 * @returns {string} HTML-escaped markup (or plain text with helpers.plain)
 */
export function renderTemplate(templateString, asset, helpers = {}) {
  const { resolve, plain = false, joiner = '<br>', maxLength = 0 } = helpers;
  const lines = String(templateString ?? '').split(/\r?\n/);
  const kept = [];

  for (const line of lines) {
    let sawVar = false;
    let sawValue = false;
    let text = '';
    let last = 0;
    TOKEN_RE.lastIndex = 0;
    let m;
    while ((m = TOKEN_RE.exec(line)) !== null) {
      text += line.slice(last, m.index);
      last = m.index + m[0].length;
      sawVar = true;
      const val = resolveVariable(m[1], asset, resolve);
      if (val !== '') { sawValue = true; text += val; }
    }
    text += line.slice(last);
    if (sawVar && !sawValue) continue;      // every variable empty → collapse
    if (!text.trim()) continue;             // nothing left to show
    kept.push(text.trim());
  }

  let joined = kept.join('\n');
  if (maxLength > 0 && joined.length > maxLength) joined = `${joined.slice(0, Math.max(1, maxLength - 1))}…`;
  if (!joined) return '';
  if (plain) return joined.split('\n').join(joiner === '<br>' ? ' ' : joiner);
  return joined.split('\n').map(escapeHtml).join(joiner);
}

// ── Candidate lists (the same order the hard-coded surfaces produce) ────────
// Both the editor and the render-path integration build their candidate set
// here, so "what columns exist" has exactly ONE definition.

/**
 * Every column the assets list can show, in the built-in order.
 * @returns {Array<{key:string,label:string,kind:string,defaultOrder:number}>}
 */
export function listColumnCandidates(regDefs = _regDefs, tenant = _tenantRecord) {
  const L = resolveFieldLabels(tenant);
  const esn = fixedTextFields(tenant).find(f => f.key === 'engine_serialNo' && f.enabled);
  const out = [
    { key: 'statusdot', label: 'Status dot', kind: 'system', defaultOrder: 10 },
    { key: 'photo',     label: 'Photo',      kind: 'system', defaultOrder: 20 },
  ];
  if (esn) out.push({ key: 'engine_serialNo', label: esn.label, kind: 'text', defaultOrder: 30 });
  out.push(
    { key: 'reference', label: L.reference, kind: 'text', defaultOrder: 40 },
    { key: 'label',     label: L.label,     kind: 'text', defaultOrder: 50 },
    { key: 'serial',    label: L.serial,    kind: 'text', defaultOrder: 60 },
  );
  visibleFields(regDefs, tenant).forEach((f, i) => {
    out.push({ key: f.fieldId, label: effectiveFieldLabel(f, tenant), kind: 'registry', defaultOrder: BAND.registry + i * 10 });
  });
  [...customTextFields(tenant), ...fixedTextFields(tenant)]
    .filter(c => c.enabled && c.key !== 'engine_serialNo')
    .forEach((cf, i) => {
      out.push({ key: cf.key, label: cf.label, kind: 'text', defaultOrder: BAND.custom + i * 10 });
    });
  out.push(
    { key: 'tenant',       label: 'Tenant',         kind: 'system', defaultOrder: 300 },
    { key: 'lastPosition', label: 'Last Position',  kind: 'system', defaultOrder: 310 },
    { key: 'lastUpdated',  label: 'Last Updated',   kind: 'system', defaultOrder: 320 },
    { key: 'devices',      label: 'Paired Devices', kind: 'system', defaultOrder: 330 },
    { key: 'actions',      label: 'Row actions',    kind: 'system', defaultOrder: 999 },
  );
  return out;
}

/**
 * Every field the asset detail page can list, in the built-in order.
 * @returns {Array<{key:string,label:string,kind:string,defaultOrder:number}>}
 */
export function assetDetailCandidates(regDefs = _regDefs, tenant = _tenantRecord) {
  const L = resolveFieldLabels(tenant);
  const out = [
    { key: 'label',     label: L.label,     kind: 'text', defaultOrder: 10 },
    { key: 'reference', label: L.reference, kind: 'text', defaultOrder: 20 },
    { key: 'serial',    label: L.serial,    kind: 'text', defaultOrder: 30 },
  ];
  visibleFields(regDefs, tenant).forEach((f, i) => {
    out.push({ key: f.fieldId, label: effectiveFieldLabel(f, tenant), kind: 'registry', defaultOrder: BAND.registry + i * 10 });
  });
  [...customTextFields(tenant), ...fixedTextFields(tenant)].filter(c => c.enabled).forEach((cf, i) => {
    out.push({ key: cf.key, label: cf.label, kind: 'text', defaultOrder: BAND.custom + i * 10 });
  });
  out.push(
    { key: 'status',   label: 'Status', kind: 'system', defaultOrder: 300 },
    { key: 'tenantId', label: 'Tenant', kind: 'system', defaultOrder: 310 },
  );
  return out;
}

/**
 * Apply an ordered-key template to a live candidate list — the integration
 * helper for list-view.js / asset-detail-view.js.
 *
 * Candidates the template never heard of (a registry field switched on after
 * the template was saved) keep their built-in position and stay VISIBLE, so a
 * new field is never silently swallowed by an old saved config.
 *
 * @param {Array<{key:string}>} candidates  built by listColumnCandidates()/assetDetailCandidates()
 * @param {object} template  { columns:[…] } or { fields:[…] }
 * @param {string} [listKey='columns']
 */
export function applyOrder(candidates, template, listKey = 'columns') {
  const list = template?.[listKey];
  const cfg = new Map((list || []).map((c, i) => [c.key, { ...c, seq: i }]));
  // Where unconfigured candidates go: after everything the template names.
  // (Mixing them into the same order space put "photo" or "serial" in the
  // middle of a carefully ordered set - 2026-09-22.)
  const TAIL = 1e6;
  const out = (candidates || [])
    .map((cand, i) => {
      const hit = cfg.get(cand.key);
      return {
        ...cand,
        visible: hit ? hit.visible !== false : true,
        fromTemplate: !!hit,
        order: hit ? hit.seq : TAIL + i,
      };
    })
    .filter(c => c.visible)
    .sort((a, b) => a.order - b.order);
  // The row menu is always the last column, wherever it was configured.
  const act = out.findIndex(c => c.key === 'actions');
  if (act >= 0 && act !== out.length - 1) out.push(...out.splice(act, 1));
  return out;
}
